// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Checkbox, { CheckboxPresentationProps } from "@store/MobiusOverrides/Checkbox/Checkbox";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useEffect, useMemo, useState, HTMLAttributes, FC } from "react";
import { css } from "styled-components";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import translate from "@insite/client-framework/Translate";
import Img from "@insite/mobius/Img";
import loadAccessoryUpsell from "@store/Framework/Store/Components/AccessoryUpsell/Handlers/loadAccessoryUpsell";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { focusStyles } from "@toolkit/Themes/common/mixins/common";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { ImagePresentationProps } from "@store/MobiusOverrides/Img/Img";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    hasMeaningfulMinimumOrderQuantity,
    resolveProductMinimumOrderQuantity,
} from "@store/Framework/Common/Utilities/productLineUtilities";
import ProductLineMinimumOrderQuantity, {
    ProductLineMinimumOrderQuantityPresentationProps,
} from "@store/Library/Components/ProductLine/ProductLineMinimumOrderQuantity";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@store/Overrides/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@store/Overrides/Components/ProductPrice";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import getAddToCartForTracking from "@store/Framework/Common/Utilities/getAddToCartForTracking";
import { inlineSeparatorStyles } from "@store/Themes/common/mixins/typography";

type AddToCartParameters = Parameters<typeof addToCart>[0];

const hasPrice = (productInfo: ProductInfo | undefined) => {
    return Boolean(productInfo?.pricing?.unitNetPrice && productInfo.pricing.unitNetPrice !== 0);
};

export interface ProductAccessoryUpsellPresentationProps extends ExpandStyles<ProductAccessoryUpsellStyles> {}

export interface ProductAccessoryUpsellProps
    extends ProductAccessoryUpsellPresentationProps,
        HTMLAttributes<HTMLElement> {
    defaultIsChecked?: boolean;
    itemCount?: number | null;
    productId: string;
    refId?: string;
    showAddToCart?: boolean;
    showCheckbox?: boolean;
    warehouse: string;
    showPartNumbers?: boolean;
}

export interface ProductAccessoryUpsellStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    productListWrapper?: InjectableCss;
    productWrapper?: InjectableCss;
    productInnerWrapper?: InjectableCss;
    checkedLink?: ClickablePresentationProps;
    checkboxWrapper?: InjectableCss;
    checkbox?: CheckboxPresentationProps;
    imageWrapper?: InjectableCss;
    imageLink?: ClickablePresentationProps;
    image?: ImagePresentationProps;
    contentWrapper?: InjectableCss;
    productPartNumbers?: ProductPartNumbersStyles;
    productTitle?: TypographyPresentationProps;
    productTitleLink?: LinkPresentationProps;
    infoWrapper?: InjectableCss;
    productPrice?: ProductPriceStyles;
    quantity?: TypographyPresentationProps;
    minimumOrderQuantity?: ProductLineMinimumOrderQuantityPresentationProps;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
}

const sharedInlineSeparatorStyles = () => inlineSeparatorStyles({ spacing: pxToRem(17), color: "currentColor" });

const partNumberValueTextProps: TypographyPresentationProps = {
    css: css`
        white-space: normal;
    `,
};

export const productAccessoryUpsellStyles: ProductAccessoryUpsellStyles = {
    title: {
        css: css`
            ${spacingStyles(null, pxToRem(8))}
            display: block;
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    productListWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: ${pxToRem(10)};
        `,
    },
    productWrapper: {
        css: css`
            position: relative;
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(6)};
            width: 100%;
            padding: ${pxToRem(6, 12)};
            border: 1px solid ${getColor("common.border")};
            border-radius: ${pxToRem(4)};
            line-height: ${x => x.theme.design.typography.lineHeight.small};

            &.checked {
                border-color: ${getColor("primary")};
            }

            ${breakpoint("sm")} {
                flex-direction: row;
                flex-wrap: wrap;
                width: auto;
            }

            ${breakpoint("lg")} {
                flex-wrap: nowrap;
            }
        `,
    },
    checkedLink: {
        css: css`
            ${focusStyles()}
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: 1;
        `,
    },
    productInnerWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: inherit;
        `,
    },
    imageWrapper: {
        css: css`
            flex-shrink: 0;
        `,
    },
    imageLink: {
        css: css`
            ${focusStyles()}
            display: block;
        `,
    },
    image: {
        css: css`
            display: block;
            width: ${pxToRem(60)};
            max-width: 100%;
            height: auto;
            margin: ${pxToRem(2, 0)};
            object-fit: contain;
        `,
    },
    productPartNumbers: {
        wrapper: {
            css: css`
                ${spacingStyles(null, pxToRem(3))}
                color: ${getColor("text.light")};
                line-height: inherit;
            `,
        },
        customerNameValueText: partNumberValueTextProps,
        erpNumberValueText: partNumberValueTextProps,
        lineNumberValueText: partNumberValueTextProps,
        manufacturerItemValueText: partNumberValueTextProps,
    },
    productTitle: {
        css: css`
            ${spacingStyles(null, pxToRem(4))}
            display: flex;
            font-weight: ${x => x.theme.design.typography.weight.medium};
        `,
    },
    productTitleLink: {
        mergeCss: true,
        css: css`
            position: relative;
            z-index: 2;
            font-weight: inherit;
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: ${pxToRem(2, 0)};
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    productPrice: {
        wrapper: {
            as: "div",
            css: css`
                ${sharedInlineSeparatorStyles()}
                flex-direction: row;
                align-items: center;
                width: auto;
                line-height: inherit;
            `,
        },
        priceWrapper: {
            css: css`
                font-weight: ${x => x.theme.design.typography.weight.base};
                line-height: inherit;
            `,
        },
        price: {
            priceText: {
                css: css`
                    line-height: inherit;
                `,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    css: css`
                        line-height: inherit;
                    `,
                },
            },
        },
    },
    quantity: {
        forwardAs: "div",
        css: css`
            ${sharedInlineSeparatorStyles()}
        `,
    },
    minimumOrderQuantity: {
        extendedStyles: {
            wrapper: {
                css: css`
                    ${sharedInlineSeparatorStyles()}
                    font-size: inherit;
                `,
            },
            label: {
                css: css`
                    font-weight: ${x => x.theme.design.typography.weight.base};
                `,
            },
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            flex-grow: 1;
            justify-content: flex-end;
            align-self: flex-end;
            width: 100%;

            ${breakpoint("sm")} {
                width: auto;
            }
        `,
    },
    addToCartButton: {
        sizeVariant: "small",
        variant: "tertiary",
        mergeCss: true,
        css: css`
            width: inherit;
        `,
    },
};

const ProductAccessoryUpsell: FC<ProductAccessoryUpsellProps> = ({
    defaultIsChecked = false,
    extendedStyles,
    overridedStyles,
    productId,
    refId,
    itemCount: baseItemCount,
    showAddToCart,
    showCheckbox,
    warehouse,
    showPartNumbers,
    ...htmlAttributes
}) => {
    const styles = useExpandProps(productAccessoryUpsellStyles, extendedStyles, overridedStyles);
    const dispatch = useAppDispatch();
    const [isChecked, setIsChecked] = useState(defaultIsChecked);
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const trackSelectItem = ga.use.trackSelectItem();
    const trackAddToCart = ga.use.trackAddToCart();
    const id = useMemo(() => `accessoryUpsell-${productId}`, [productId]);
    const itemCount = baseItemCount ?? 1;

    const productsByProductId = useAppSelector(state => state.data.products.byId);
    const productInfosByProductId = useAppSelector(
        state => state.components.productInfoLists.productInfoListById[id]?.productInfoByProductId,
    );

    const productContexts = useMemo(() => {
        if (!productInfosByProductId) {
            return undefined;
        }

        return Object.keys(productInfosByProductId).reduce<ProductContextModel[]>((result, productId) => {
            const product = productsByProductId[productId];
            const productInfo = productInfosByProductId[productId];

            if (!product || !productInfo || !hasPrice(productInfo)) {
                return result;
            }

            return [...result, { product, productInfo }];
        }, []);
    }, [productsByProductId, productInfosByProductId]);

    useEffect(() => {
        if (productId && warehouse) {
            dispatch(loadAccessoryUpsell({ id, productId, warehouse }));
        }
    }, [id, productId, warehouse]);

    if (!productContexts?.length) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} {...htmlAttributes}>
            <Typography {...styles.title}>{translate("Add Compatible Accessories")}</Typography>
            <StyledWrapper {...styles.productListWrapper}>
                {productContexts.map(productContext => {
                    const { product, productInfo } = productContext;
                    const quantityPerItem = 1; // A quantity of 1 will never show, but the logic was kept in case this is computed dynamically eventually
                    const minimumOrderQuantity = resolveProductMinimumOrderQuantity(product, productInfo);
                    const image = <Img {...styles.image} altText={product.imageAltText} src={product.smallImagePath} />;

                    return (
                        <StyledWrapper
                            {...styles.productWrapper}
                            className={showCheckbox && isChecked ? "checked" : undefined}
                            key={product.id}
                        >
                            {showCheckbox && (
                                <Clickable
                                    {...styles.checkedLink}
                                    aria-pressed={showCheckbox ? isChecked : undefined}
                                    onClick={() => {
                                        setIsChecked(!isChecked);
                                        dispatch({
                                            type: "Greif/Pages/ProductDetails/SelectAccessory",
                                            selected: !isChecked,
                                            product: productInfo,
                                        });
                                    }}
                                >
                                    <VisuallyHidden>{product.productTitle}</VisuallyHidden>
                                </Clickable>
                            )}
                            <StyledWrapper {...styles.productInnerWrapper}>
                                {showCheckbox && (
                                    <StyledWrapper {...styles.checkboxWrapper} aria-hidden="true">
                                        <Checkbox
                                            {...styles.checkbox}
                                            checked={isChecked}
                                            onChange={() => {} /* required for checked state to update */}
                                            tabIndex={-1}
                                        />
                                    </StyledWrapper>
                                )}
                                <StyledWrapper {...styles.imageWrapper}>
                                    {!showCheckbox && productInfo.productDetailPath ? (
                                        <Clickable
                                            {...styles.imageLink}
                                            href={productInfo.productDetailPath}
                                            onClick={() => trackSelectItem({ items: product })}
                                        >
                                            {image}
                                        </Clickable>
                                    ) : (
                                        image
                                    )}
                                </StyledWrapper>
                                <StyledWrapper {...styles.contentWrapper}>
                                    {showPartNumbers && (
                                        <ProductPartNumbers
                                            customerProductNumber={product.customerProductNumber}
                                            extendedStyles={styles.productPartNumbers}
                                            manufacturerItem={product.manufacturerItem}
                                            productNumber={product.productNumber}
                                            showCustomerName={false}
                                            showManufacturerItem={false}
                                        />
                                    )}
                                    <Typography {...styles.productTitle}>
                                        {productInfo.productDetailPath ? (
                                            <Link
                                                {...styles.productTitleLink}
                                                href={productInfo.productDetailPath}
                                                onClick={() => trackSelectItem({ items: product })}
                                            >
                                                {product.productTitle}
                                            </Link>
                                        ) : (
                                            product.productTitle
                                        )}
                                    </Typography>
                                    <StyledWrapper {...styles.infoWrapper}>
                                        <ProductPrice
                                            extendedStyles={styles.productPrice}
                                            product={productContext}
                                            showLabel={false}
                                        />
                                        {quantityPerItem > 1 && (
                                            <Typography
                                                {...styles.quantity}
                                                data-test-selector="accessoryUpsell_quantity"
                                            >
                                                {translate("Qty: {0} per item", `${quantityPerItem}`)}
                                            </Typography>
                                        )}
                                        {hasMeaningfulMinimumOrderQuantity(minimumOrderQuantity) && (
                                            <ProductLineMinimumOrderQuantity
                                                {...styles.minimumOrderQuantity}
                                                minimumOrderQuantity={minimumOrderQuantity}
                                            />
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                            {showAddToCart && (
                                <StyledWrapper {...styles.addToCartWrapper}>
                                    <Button
                                        {...styles.addToCartButton}
                                        isLoading={isAddingToCart}
                                        disabled={isAddingToCart}
                                        onClick={async () => {
                                            setIsAddingToCart(true);

                                            const parameters: AddToCartParameters = {
                                                productId: product.id,
                                                qtyOrdered: quantityPerItem * itemCount,
                                                unitOfMeasure: productInfo.unitOfMeasure,
                                                properties: {},
                                            };

                                            if (refId) {
                                                parameters.properties!.parentRefId = refId;
                                            }

                                            try {
                                                const cartLine = await dispatch(
                                                    makeHandlerChainAwaitable<AddToCartParameters, CartLineModel>(
                                                        addToCart,
                                                    )(parameters),
                                                );

                                                trackAddToCart(getAddToCartForTracking([cartLine]));
                                            } finally {
                                                setIsAddingToCart(false);
                                            }
                                        }}
                                    >
                                        {translate("Add to Cart")}
                                    </Button>
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default ProductAccessoryUpsell;
